import { z } from "zod"

import type { CompanyListFilter, ContactQueryableFields, LocationListFilter } from "@/graphql/codegen/graphql"
import {
  CompanyListCertificationSortField,
  CompanyListFilterSchema,
  CompanySortField,
  CompanyStatus,
  ContactQueryableFieldsSchema,
  ContactSortField,
  CustomerAccountStatus,
  LocationListFilterSchema,
  LocationSortField,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { SearchCategory } from "@/screens/Companies/types/enums"
import { CUSTOMER_COMPANY_TYPES, type CompanyType } from "@/types/companyType"
import { isNil } from "@/utils/utils"

// AK-6592 AC6: The default statuses apply only until the user picks statuses or clears the filters. Clearing keeps an
// empty selection in the URL, so these keys must survive being empty.
export const COMPANY_EXPLICIT_EMPTY_FILTER_KEYS = ["statuses", "customerAccountStatuses"]

const DEFAULT_COMPANY_STATUSES = [CompanyStatus.Active, CompanyStatus.Pending]
const DEFAULT_CUSTOMER_ACCOUNT_STATUSES = [CustomerAccountStatus.Active, CustomerAccountStatus.CreditHold]

const hasCustomerType = (types: string[] | null | undefined): boolean =>
  Boolean(types?.some((type) => CUSTOMER_COMPANY_TYPES.includes(type as CompanyType)))

type CompanySearchDefaults = Pick<CompanyListFilter, "statuses" | "customerAccountStatuses" | "types">

// Customer account status only applies to customers, so vendors are not hidden by it.
const toCompanyListDefaults = ({
  statuses,
  customerAccountStatuses,
  types,
}: CompanySearchDefaults): Pick<CompanySearchDefaults, "statuses" | "customerAccountStatuses"> => ({
  statuses: isNil(statuses) ? DEFAULT_COMPANY_STATUSES : statuses,
  customerAccountStatuses:
    isNil(customerAccountStatuses) && hasCustomerType(types)
      ? DEFAULT_CUSTOMER_ACCOUNT_STATUSES
      : customerAccountStatuses,
})

export const SearchParamsSchema = z
  .object({
    ...ContactQueryableFieldsSchema().shape,
    ...LocationListFilterSchema().shape,
    ...CompanyListFilterSchema().shape,
    field: z
      .union([
        z.nativeEnum(CompanySortField),
        z.nativeEnum(ContactSortField),
        z.nativeEnum(LocationSortField),
        z.nativeEnum(CompanyListCertificationSortField),
      ] as const)
      .optional()
      .default(CompanySortField.Name),
    category: z.nativeEnum(SearchCategory).default(SearchCategory.COMPANY),
    order: z.nativeEnum(SortingOrder).default(SortingOrder.Asc),
    query: z.string().optional().default(""),
  })
  // Defaults apply to the company list only: the contact and location lists spread all search params into their queries.
  .transform((params) =>
    params.category === SearchCategory.COMPANY ? { ...params, ...toCompanyListDefaults(params) } : params
  )

export type SearchParams = z.infer<typeof SearchParamsSchema>

export type CompanySearchParams = {
  category: SearchCategory
  field?: CompanySortField
  order?: SortingOrder
} & { [key in keyof CompanyListFilter]?: CompanyListFilter[key] }

export type ContactSearchParams = {
  category: SearchCategory
  field?: ContactSortField
  order?: SortingOrder
} & { [key in keyof ContactQueryableFields]?: ContactQueryableFields[key] }

export type LocationSearchParams = {
  category: SearchCategory
  field?: LocationSortField
  order?: SortingOrder
} & { [key in keyof LocationListFilter]?: LocationListFilter[key] }
